import React, { useState } from 'react';
import { Box, Typography, Container, TextField, Snackbar } from '@mui/material';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { viewportOnce, ease } from '../motion';
import { profile } from '../content';
import SectionHeading from './SectionHeading';
import MagneticButton from './MagneticButton';

const socialIcon = (label: string) => {
  switch (label) {
    case 'GitHub':
      return <GitHubIcon />;
    case 'LinkedIn':
      return <LinkedInIcon />;
    case 'Twitter':
      return <TwitterIcon />;
    default:
      return <GitHubIcon />;
  }
};

const Contact: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: wire up to a real backend / form service (Formspree, Resend, etc.)
    console.log('Form submitted:', form);
    setSent(true);
    setForm({ name: '', email: '', message: '' });
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Box component="section" id="contact" sx={{ py: { xs: 10, md: 18 }, position: 'relative' }}>
      <Container maxWidth="lg" sx={{ px: { xs: 3, md: 6 } }}>
        <SectionHeading
          index="05"
          eyebrow="contact"
          title="Let's build something"
          subtitle="Have a project in mind, or just want to say hi? My inbox is always open."
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' },
            gap: { xs: 6, md: 10 },
          }}
        >
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.6, ease: ease.out }}
          >
            <Box component="form" onSubmit={onSubmit}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2.5, mb: 2.5 }}>
                <TextField fullWidth required name="name" label="Name" value={form.name} onChange={onChange} />
                <TextField fullWidth required name="email" type="email" label="Email" value={form.email} onChange={onChange} />
              </Box>
              <TextField
                fullWidth
                required
                name="message"
                label="Message"
                multiline
                rows={6}
                value={form.message}
                onChange={onChange}
                sx={{ mb: 3 }}
              />
              <MagneticButton type="submit" variant="contained">
                Send message
              </MagneticButton>
            </Box>
          </motion.div>

          {/* Direct + socials */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.6, delay: 0.1, ease: ease.out }}
          >
            <Typography
              sx={{ fontFamily: (t) => t.typography.button.fontFamily, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'text.secondary', mb: 1.5 }}
            >
              Email
            </Typography>
            <Box
              onClick={copyEmail}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                p: 2,
                mb: 4,
                borderRadius: 2,
                border: (t) => `1px solid ${t.palette.divider}`,
                cursor: 'pointer',
                transition: 'border-color 0.25s ease',
                '&:hover': { borderColor: (t) => `${t.palette.primary.main}55` },
              }}
            >
              <Typography sx={{ fontFamily: (t) => t.typography.button.fontFamily, fontSize: '0.9rem', color: 'text.primary', wordBreak: 'break-all' }}>
                {profile.email}
              </Typography>
              <Box sx={{ color: copied ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                {copied ? <CheckIcon sx={{ fontSize: '1.1rem' }} /> : <ContentCopyIcon sx={{ fontSize: '1.1rem' }} />}
              </Box>
            </Box>

            <Typography
              sx={{ fontFamily: (t) => t.typography.button.fontFamily, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'text.secondary', mb: 1.5 }}
            >
              Elsewhere
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {profile.socials.map((s) => (
                <Box
                  key={s.label}
                  component="a"
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    color: 'text.secondary',
                    textDecoration: 'none',
                    border: '1px solid transparent',
                    transition: 'all 0.25s ease',
                    '&:hover': { color: 'text.primary', borderColor: (t) => t.palette.divider, transform: 'translateX(4px)' },
                    '& svg': { fontSize: '1.2rem' },
                  }}
                >
                  {socialIcon(s.label)}
                  <Typography sx={{ flexGrow: 1, fontSize: '0.9rem' }}>{s.label}</Typography>
                  <Typography sx={{ fontFamily: (t) => t.typography.button.fontFamily, fontSize: '0.78rem', color: 'text.disabled' }}>
                    {s.handle}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Box>
      </Container>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Email copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={sent}
        autoHideDuration={3000}
        onClose={() => setSent(false)}
        message="Thanks! I'll get back to you soon."
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Contact;
